import { apiClient, getErrorMessage } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import {
  API_USER_APP_ACCESS_MARK_ACCESSED,
  API_USER_APP_ACCESS_RECENTS,
  API_USER_APP_ACCESS_TOGGLE_FAVORITE,
  API_USER_APP_ACCESS_FAVORITES,
} from '../../constants/apiConstants';
import type { SysAppDto } from './sysAppService';

export interface UserAppAccessSummaryDto extends SysAppDto {
  lastAccessedAt: string;
  accessCount: number;
  isFavorite: boolean;
  favoritedAt: string | null;
}

export interface MarkAccessedResponse {
  userAppAccessId: number;
  lastAccessedAt: string;
  accessCount: number;
}

export interface ToggleFavoriteResponse {
  sysAppId: number;
  isFavorite: boolean;
  favoritedAt: string | null;
}

export const userAppAccessService = {
  /**
   * Record a tile-click. tenantId is the caller's current active tenant context
   * (null = non-tenant surface). The backend does NOT read this from UserPreference
   * because the tenant switcher doesn't sync to DB — the client must pass it.
   */
  async markAccessed(sysAppId: number, tenantId: number | null): Promise<MarkAccessedResponse> {
    try {
      const response = await apiClient.post(API_USER_APP_ACCESS_MARK_ACCESSED, { sysAppId, tenantId });
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Mark-accessed failed');
      }
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to mark app accessed', error, { sysAppId, tenantId });
      throw new Error(msg);
    }
  },

  async getRecents(tenantId: number | null, take = 6): Promise<UserAppAccessSummaryDto[]> {
    try {
      const params: Record<string, number> = { take };
      if (tenantId !== null) params.tenantId = tenantId;
      const response = await apiClient.get(API_USER_APP_ACCESS_RECENTS, { params });
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Get-recents failed');
      }
      return response.data.data?.items ?? [];
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to fetch recents', error, { tenantId });
      throw new Error(msg);
    }
  },

  /**
   * Flip the favorite flag for the given app within the caller's active tenant
   * context. Server is idempotent-to-target-state: the returned `isFavorite`
   * reflects the post-toggle state. Null tenantId targets the null-tenant bucket.
   */
  async toggleFavorite(sysAppId: number, tenantId: number | null): Promise<ToggleFavoriteResponse> {
    try {
      const response = await apiClient.post(API_USER_APP_ACCESS_TOGGLE_FAVORITE, { sysAppId, tenantId });
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Toggle-favorite failed');
      }
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to toggle favorite', error, { sysAppId, tenantId });
      throw new Error(msg);
    }
  },

  async getFavorites(tenantId: number | null): Promise<UserAppAccessSummaryDto[]> {
    try {
      const params: Record<string, number> = {};
      if (tenantId !== null) params.tenantId = tenantId;
      const response = await apiClient.get(API_USER_APP_ACCESS_FAVORITES, { params });
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Get-favorites failed');
      }
      return response.data.data?.items ?? [];
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to fetch favorites', error, { tenantId });
      throw new Error(msg);
    }
  },
};
